import { useMemo } from "react";
import { useMediaQuery } from "react-responsive";

import { Card, Heading, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { screenSizes } from "consts";
import { type KeystoneReferral } from "data/keystone-api-mapping";
import { capitalize, trackEvent } from "utils";

import { useInspectionsData } from "../hooks";
import { InspectionThumbnailStrip } from "../inspection-thumbnail-strip";
import { sortInspectionAssetIdsByLastUpdatedDesc } from "../inspections-helpers";
import {
  formatReferralWriteOutCardSubheading,
  getReferralReasonings,
  getReferralWriteOutCardHeading,
} from "../referral-display";
import styles from "./ineligible-write-out-card.module.scss";

export const IneligibleWriteOutCard = ({
  referral,
  showGallery,
}: {
  referral: KeystoneReferral;
  showGallery: (activeAssetId?: string) => void;
}) => {
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const maxPhotosInRow = isMobile ? 3 : 5;
  const { inspectionReviewAssetIds } = referral;
  const { latestInspection } = useInspectionsData();
  const inspectionReviewAssets =
    latestInspection?.review?.inspectionReviewAssets;
  const sortedInspectionAssetIds = useMemo(
    () =>
      sortInspectionAssetIdsByLastUpdatedDesc(
        inspectionReviewAssetIds ?? [],
        inspectionReviewAssets,
      ),
    [inspectionReviewAssetIds, inspectionReviewAssets],
  );

  const writeOutHeading = getReferralWriteOutCardHeading(referral);
  const reasonings = getReferralReasonings(referral);
  const subHeading = formatReferralWriteOutCardSubheading(referral);

  const openGallery = (activeAssetId?: string) => {
    trackEvent("inspections ineligible gallery opened", {
      numberOfAssets: sortedInspectionAssetIds.length,
      writeOutHeading: capitalize(writeOutHeading ?? ""),
    });
    showGallery(activeAssetId);
  };

  const orderedThumbnailItems = useMemo(
    () =>
      sortedInspectionAssetIds.map((id) => ({
        id,
        asset: inspectionReviewAssets?.find((a) => a.id === id),
      })),
    [inspectionReviewAssets, sortedInspectionAssetIds],
  );

  const renderAssetList = () => (
    <InspectionThumbnailStrip
      heading="Inspection photos"
      maxPhotosInRow={maxPhotosInRow}
      onPreviewClick={openGallery}
      orderedItems={orderedThumbnailItems}
    />
  );

  return (
    <Card>
      <Stack gap="sm">
        <div className={styles.heading}>
          <div className={styles.titleAndSubheading}>
            {writeOutHeading ? (
              <Heading size="sm">{capitalize(writeOutHeading)}</Heading>
            ) : null}
            <Span color="quiet" size="sm">
              {subHeading}
            </Span>
          </div>
        </div>

        {reasonings.length ? (
          <div className={styles.reasoning}>
            <Span bold>Why is this an issue?</Span>
            {reasonings.map((reasoningString, idx) => (
              <Span key={idx}>{reasoningString}</Span>
            ))}
          </div>
        ) : null}

        {renderAssetList()}
      </Stack>
    </Card>
  );
};
